import { A } from '@adornis/base/env-info.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { from, map, switchMap } from 'rxjs';
import { MatchingWeekRole } from '../db/enums/roles.js';
import { MatchingWeekSettings } from '../db/settings.js';

const ensureOneMatchingWeekSettings = async () => {
  const collection = mongo().collection(MatchingWeekSettings._collectionName);
  const existingSettings = await collection.findOneAndUpdate(
    {},
    {
      $setOnInsert: {
        _class: MatchingWeekSettings._class,
        _id: A.getGloballyUniqueID(),
        imprint: [],
        privacyPolicy: [],
        companyTerms: [],
        applicationPhaseClosed: true,
        companyRegistrationClosed: false,
      },
    },
    { upsert: true, returnDocument: 'after' },
  );
  return existingSettings;
};

export const subscribeMatchingWeekSettings = registerSubscription({
  operationName: 'subscribeMatchingWeekSettings',
  type: () => MatchingWeekSettings,
  resolve: () => (gqlFields: BaseQLSelectionSet<MatchingWeekSettings>) => {
    return from(
      (async () => {
        await ensureOneMatchingWeekSettings();
        if (context.serverContext) return true;
        const user = await context.user();
        return user?.roles?.some(role => role.name === MatchingWeekRole.ADMIN) ?? false;
      })(),
    ).pipe(
      switchMap(isAdmin =>
        watchQuery<EntityData<MatchingWeekSettings>>(
          MatchingWeekSettings._collectionName,
          {},
          isAdmin
            ? {}
            : {
                projection: {
                  _id: 1,
                  _class: 1,
                  imprint: 1,
                  privacyPolicy: 1,
                  companyTerms: 1,
                  participationAgreementFileId: 1,
                  participationAgreementDate: 1,
                  primaryColor: 1,
                  primaryContentColor: 1,
                  applicationPhaseClosed: 1,
                  companyRegistrationClosed: 1,
                },
              },
        ),
      ),
      map(settings => settings[0]),
    );
  },
});
